// src/pages/chat/sidebar/bottom-area/bottom-area.tsx
/** @jsxImportSource @emotion/react */
import { useMemo } from 'react';

import TalkToChrisModal from '../../header/modals/talk-to-chris/talk-to-chris';
import {
  BottomAreaContainer,
  ActionsList,
  ActionButton,
  LogoutButton,
  UserCard,
  UserInfo,
  UserName,
} from './bottom-area.styled';
import { useDrawerBottomArea, type DrawerBottomAreaProps } from './bottom-area.hooks';
import { useAuth } from '../../../../contexts/auth/auth-context';
import { useOnboarding } from '../../../../contexts/onboarding-context';
import { AppAvatar } from '../../../../components/avatar/avatar';
import { usePressable } from '../../../../hooks/use-pressable';

type Props = DrawerBottomAreaProps;

export default function DrawerBottomArea(restProps: Props) {
  const {
    talkToChrisOpen,
    handleSettingsClick,
    handleTalkToChrisClick,
    handleNotificationsClick,
    handleDocsClick,
    handleLogoutClick,
    handleTalkToChrisOpenChange,
    openUrgentFromTalk,
  } = useDrawerBottomArea(restProps);

  const { user, profile, signOut } = useAuth();
  const { isCompleted: onboardingCompleted } = useOnboarding();

  const firstName = String(profile?.first_name ?? '').trim();
  const lastName = String(profile?.last_name ?? '').trim();

  const displayName = useMemo(() => {
    const full = `${firstName} ${lastName}`.trim();
    if (full) return full;

    const nick = String(profile?.nickname ?? '').trim();
    if (nick) return nick;

    const emailFromProfile = String((profile as any)?.email_address ?? '').trim();
    if (emailFromProfile) return emailFromProfile;

    const emailFromUser = String((user as any)?.email ?? '').trim();
    if (emailFromUser) return emailFromUser;

    return 'Account';
  }, [firstName, lastName, profile, user]);

  const initials = useMemo(() => {
    const c = firstName.charAt(0) || displayName.charAt(0) || '?';
    return c.toUpperCase();
  }, [firstName, displayName]);

  const onLogout = async () => {
    try {
      await Promise.resolve(handleLogoutClick?.());
    } catch {
      // ignore
    }
    await signOut();
  };

  const pressSettings = usePressable();
  const pressNotifications = usePressable();
  const pressTalk = usePressable();
  const pressDocs = usePressable();
  const pressLogout = usePressable();

  return (
    <>
      <BottomAreaContainer>
        <ActionsList>
          <ActionButton onClick={handleSettingsClick} {...pressSettings}>Settings</ActionButton>
          <ActionButton onClick={handleNotificationsClick} {...pressNotifications}>Notifications</ActionButton>
          <ActionButton onClick={handleTalkToChrisClick} disabled={!onboardingCompleted} {...pressTalk}>Talk to Chris</ActionButton>
          <ActionButton onClick={handleDocsClick} {...pressDocs}>My files</ActionButton>
          <LogoutButton onClick={onLogout} {...pressLogout}>Log out</LogoutButton>
        </ActionsList>

        <UserCard>
          <AppAvatar
            userId={String(profile?.id ?? '')}
            version={(profile as any)?.avatar_version ?? (profile as any)?.updated_at ?? null}
            hasAvatar={!!profile?.avatar_url}
            alt="You"
            initials={initials}
            width={34}
            height={34}
            imgStyle={{ borderRadius: '999px', objectFit: 'cover' }}
            fallbackStyle={{ borderRadius: '999px' }}
          />

          <UserInfo>
            <UserName>{displayName}</UserName>
          </UserInfo>
        </UserCard>
      </BottomAreaContainer>

      <TalkToChrisModal
        open={talkToChrisOpen}
        onOpenChange={handleTalkToChrisOpenChange}
        onOpenUrgent={openUrgentFromTalk}
      />
    </>
  );
}
